// ModOrdersPanel — admin-only queue of paid tune orders with mod_options.
// Shows what's auto-applied vs what needs DAMOS lookup, lets admin pick a
// base .bin, applies the mods, and surfaces the mod log + download URL.
import React, { useState, useEffect, useCallback } from 'react';
import {
  Wrench, Loader2, Download, AlertTriangle, CheckCircle2,
  ChevronDown, ChevronRight, RefreshCw, Database,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  verified:           { color: 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10', label: 'APPLIED' },
  'no-op':            { color: 'text-zinc-400 border-zinc-500/40 bg-zinc-500/10',           label: 'NO-OP' },
  needs_damos_lookup: { color: 'text-amber-400 border-amber-500/40 bg-amber-500/10',        label: 'NEEDS DAMOS' },
  error:              { color: 'text-red-400 border-red-500/40 bg-red-500/10',              label: 'ERROR' },
};

function authHeaders() {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Common base .bin files in /app/. Admin can also type a custom name.
const COMMON_BASES = [
  'BMW_E90_328xi_E85_V12_SAFE.bin',
  'BMW_E90_328xi_E85_V11.bin',
  'stock_0MK80S.bin',
];

export default function ModOrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [registry, setRegistry] = useState({ verified: [], needs_damos: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);   // tx id
  const [applyState, setApplyState] = useState({}); // tx id → {loading, base, result}

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/mod-orders`, { headers: { ...authHeaders() } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setOrders(data.orders || []);
      setRegistry(data.registry || { verified: [], needs_damos: [] });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const applyMods = async (tx, baseFilename) => {
    setApplyState((s) => ({ ...s, [tx.id]: { ...s[tx.id], loading: true, base: baseFilename } }));
    try {
      const res = await fetch(`${API_URL}/api/admin/mod-orders/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ transaction_id: tx.id, base_filename: baseFilename }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail?.error || data.detail || `HTTP ${res.status}`);
      }
      setApplyState((s) => ({ ...s, [tx.id]: { loading: false, base: baseFilename, result: data } }));
      refresh();
    } catch (err) {
      setApplyState((s) => ({ ...s, [tx.id]: { loading: false, base: baseFilename, result: { error: err.message } } }));
    }
  };

  return (
    <div className="bg-[#111111] border border-[#27272A] rounded-sm p-5" data-testid="mod-orders-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Wrench className="w-5 h-5 text-[#FF5E1A]" />
          <h3 className="font-['Outfit'] text-lg font-black text-white">Tune Mod Orders</h3>
          <span className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-wider px-1.5 py-0.5 border border-zinc-500/40 bg-zinc-500/10 text-zinc-400 rounded-sm">
            paid · awaiting fulfillment
          </span>
        </div>
        <button
          onClick={refresh}
          data-testid="mod-orders-refresh"
          className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] font-bold tracking-wider uppercase text-zinc-400 hover:text-white border border-[#27272A] hover:border-[#FF5E1A] rounded-sm transition-colors"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Refresh
        </button>
      </div>

      {/* Registry snapshot */}
      <div className="mb-5 p-3 border border-[#27272A] bg-[#0A0A0A] rounded-sm">
        <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.16em] text-zinc-500 mb-2 flex items-center gap-1.5">
          <Database className="w-3 h-3" /> Mod registry status
        </div>
        <div className="grid sm:grid-cols-2 gap-3 text-xs">
          <div>
            <div className="text-emerald-400 mb-1">
              <CheckCircle2 className="w-3 h-3 inline mr-1" />
              Auto-applied ({registry.verified.length})
            </div>
            <div className="text-zinc-400 font-['JetBrains_Mono'] text-[10px]">
              {registry.verified.length === 0 ? '(none — addresses pending DAMOS verification)' :
                registry.verified.map((m) => m.id).join(', ')}
            </div>
          </div>
          <div>
            <div className="text-amber-400 mb-1">
              <AlertTriangle className="w-3 h-3 inline mr-1" />
              Manual ({registry.needs_damos.length})
            </div>
            <div className="text-zinc-400 font-['JetBrains_Mono'] text-[10px]">
              {registry.needs_damos.map((m) => m.id).join(', ')}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-3 p-2 border border-red-500/40 bg-red-500/10 rounded-sm text-xs text-red-300">{error}</div>
      )}

      {!loading && orders.length === 0 && (
        <div className="text-xs text-zinc-500 italic py-3">No paid orders with mods yet.</div>
      )}

      <div className="space-y-2">
        {orders.map((tx) => {
          const isOpen = expanded === tx.id;
          const mods = tx.mod_options?.mods || [];
          const isPaid = tx.payment_status === 'paid' || tx.status === 'paid';
          const fulfilled = !!tx.patched_bin_filename;
          const apply = applyState[tx.id];

          return (
            <div
              key={tx.id}
              className="border border-[#27272A] bg-[#0A0A0A] rounded-sm"
              data-testid={`mod-order-row-${tx.id}`}
            >
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : tx.id)}
                className="w-full flex items-start gap-3 p-3 text-left hover:bg-[#FF5E1A]/5 transition-colors"
              >
                {isOpen ? <ChevronDown className="w-4 h-4 mt-0.5 text-zinc-500" /> : <ChevronRight className="w-4 h-4 mt-0.5 text-zinc-500" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-sm text-white font-bold truncate">{tx.package_name}</span>
                    <span className={`text-[9px] font-['JetBrains_Mono'] px-1.5 py-0.5 border rounded-sm uppercase tracking-wider ${
                      isPaid ? 'text-emerald-400 border-emerald-500/40 bg-emerald-500/10'
                             : 'text-amber-400 border-amber-500/40 bg-amber-500/10'
                    }`}>
                      {isPaid ? 'paid' : (tx.payment_status || 'pending')}
                    </span>
                    {fulfilled && (
                      <span className="text-[9px] font-['JetBrains_Mono'] px-1.5 py-0.5 border border-[#FF5E1A]/40 bg-[#FF5E1A]/10 text-[#FF5E1A] rounded-sm uppercase tracking-wider">
                        patched
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-zinc-400 font-['JetBrains_Mono']">
                    ${tx.amount} · {mods.length} mod{mods.length === 1 ? '' : 's'} ·{' '}
                    {tx.mod_options?.vin || 'no VIN'} · {tx.mod_options?.intake || '—'} ·{' '}
                    {(tx.created_at || '').slice(0, 10)}
                  </div>
                </div>
              </button>

              {isOpen && (
                <div className="border-t border-[#27272A] p-3 space-y-3">
                  {/* Selected mods */}
                  <div>
                    <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.16em] text-zinc-500 mb-1">
                      Selected mods
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {mods.map((m) => (
                        <span key={m} className="text-[10px] font-['JetBrains_Mono'] px-2 py-0.5 border border-[#27272A] bg-[#0F0F0F] text-zinc-300 rounded-sm">
                          {m}
                        </span>
                      ))}
                    </div>
                  </div>
                  {tx.mod_options?.notes && (
                    <div>
                      <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.16em] text-zinc-500 mb-1">
                        Customer notes
                      </div>
                      <div className="text-xs text-zinc-300 italic">"{tx.mod_options.notes}"</div>
                    </div>
                  )}

                  {/* Apply mods controls */}
                  {!fulfilled && (
                    <div className="bg-[#0F0F0F] border border-[#27272A] rounded-sm p-3">
                      <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.16em] text-zinc-500 mb-2">
                        Apply mods to base .bin
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {COMMON_BASES.map((b) => (
                          <button
                            key={b}
                            onClick={() => applyMods(tx, b)}
                            disabled={apply?.loading}
                            data-testid={`apply-mods-${tx.id}-${b}`}
                            className="text-[10px] font-['JetBrains_Mono'] px-2.5 py-1.5 bg-[#FF5E1A] hover:bg-[#FF7A42] disabled:opacity-40 text-white rounded-sm transition-colors"
                          >
                            {apply?.loading && apply?.base === b ? <Loader2 className="w-3 h-3 animate-spin inline" /> : b}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Mod log (from this apply OR previous fulfillment) */}
                  {(apply?.result?.log || tx.applied_mod_log) && (
                    <div>
                      <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.16em] text-zinc-500 mb-2">
                        Mod log
                      </div>
                      <div className="space-y-1">
                        {(apply?.result?.log || tx.applied_mod_log || []).map((l, idx) => {
                          const s = STATUS_STYLE[l.status] || STATUS_STYLE.error;
                          return (
                            <div
                              key={`${l.mod_id}-${idx}`}
                              className="flex items-start gap-2 p-2 bg-[#0F0F0F] border border-[#27272A] rounded-sm"
                            >
                              <span className={`text-[9px] font-['JetBrains_Mono'] px-1.5 py-0.5 border rounded-sm whitespace-nowrap ${s.color}`}>
                                {s.label}
                              </span>
                              <div className="flex-1 min-w-0">
                                <div className="text-xs text-white font-bold">{l.title}</div>
                                <div className="text-[11px] text-zinc-400 leading-relaxed">{l.note}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Download patched */}
                  {(apply?.result?.download_url || tx.patched_bin_filename) && (
                    <a
                      href={`${API_URL}${apply?.result?.download_url || `/api/bin/download/${tx.patched_bin_filename}`}`}
                      data-testid={`download-patched-${tx.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#FF5E1A] hover:bg-[#FF7A42] text-white text-[10px] font-bold tracking-wider uppercase rounded-sm transition-colors"
                    >
                      <Download className="w-3 h-3" />
                      Download {apply?.result?.patched_filename || tx.patched_bin_filename}
                    </a>
                  )}

                  {apply?.result?.error && (
                    <div className="p-2 border border-red-500/40 bg-red-500/10 rounded-sm text-xs text-red-300">
                      {apply.result.error}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
